import { generatePath } from '@helpers';
import { config } from '@terminal/config/app.config';
import { useQueryParams } from '@terminal/hooks/use-query-params/useQueryParams';
import { appPaths } from '@terminal/routes/app-paths';
import { queryKeys, queryValues } from '@terminal/routes/query-params';
import { PosthogTrackingSource } from '@terminal/services/tracking/posthog/constants';
import { useSubscription } from '@terminal/stores/use-subscription/useSubscription';
import { create } from '@terminal/utils/store/createStore';
import { useNavigate } from 'react-router';

const modes = ['quickAnalysis', 'document', 'research'] as const;
export type ChatMode = (typeof modes)[number];

type ChatModeStore = {
  chatMode: ChatMode;
  previousMode: ChatMode | null;
  alfredTrackingSource: PosthogTrackingSource | null;
  newConversationSource: PosthogTrackingSource | null;
  setChatMode: (chatMode: ChatMode) => void;
  resetPreviousMode: () => void;
  setAlfredTrackingSource: (source: PosthogTrackingSource) => void;
  clearAlfredTrackingSource: () => void;
  setNewConversationSource: (source: PosthogTrackingSource) => void;
  clearNewConversationSource: () => void;
};

export const useChatModeStore = create<ChatModeStore>(set => ({
  chatMode: 'research',
  previousMode: null,
  alfredTrackingSource: null,
  newConversationSource: null,
  setChatMode: chatMode =>
    set(state => ({
      previousMode: state.chatMode,
      chatMode,
    })),
  resetPreviousMode: () => set({ previousMode: null }),
  setAlfredTrackingSource: (source: PosthogTrackingSource) =>
    set({ alfredTrackingSource: source }),
  clearAlfredTrackingSource: () => set({ alfredTrackingSource: null }),
  setNewConversationSource: (source: PosthogTrackingSource) =>
    set({ newConversationSource: source }),
  clearNewConversationSource: () => set({ newConversationSource: null }),
}));

export const useChatNavigation = () => {
  const {
    chatMode,
    previousMode,
    setChatMode,
    resetPreviousMode,
    alfredTrackingSource,
    setAlfredTrackingSource,
    clearAlfredTrackingSource,
  } = useChatModeStore();
  const { params, setQueryParams } = useQueryParams();
  const navigate = useNavigate();
  const isBasicUser = useSubscription(state => state.isBasicUser());

  const navigateToAlfred = (
    mode: ChatMode,
    options: {
      searchParams?: URLSearchParams;
      trackingSource?: PosthogTrackingSource;
    } = {}
  ) => {
    const { searchParams = new URLSearchParams(), trackingSource } = options;

    if (trackingSource) {
      setAlfredTrackingSource(trackingSource);
    }

    setMode(mode);

    navigate({
      pathname: appPaths.alfred,
      search: searchParams.toString(),
    });
  };

  const setMode = (mode: ChatMode) => {
    const onAlfredPage = appPaths.alfred === location.pathname;

    if (onAlfredPage) {
      setQueryParams({ [queryKeys.mode]: mode }, { replace: true });
    }

    setChatMode(mode);
  };

  const navigateToConversation = (
    conversationId: string,
    options: {
      newTab?: boolean;
      trackingSource?: PosthogTrackingSource;
    } = {}
  ) => {
    const { newTab = false, trackingSource } = options;

    if (trackingSource) {
      setAlfredTrackingSource(trackingSource);
    }

    const pathAndQuery = generatePath({
      basePath: appPaths.alfred,
      params: {
        [queryKeys.mode]: queryValues.deepResearchMode,
        [queryKeys.conversationId]: conversationId,
      },
    });

    if (newTab) {
      const absoluteUrl = `${config.urls.webAppOrigin}${pathAndQuery}`;
      window.open(absoluteUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    navigate(pathAndQuery);
  };

  const getMode = (): ChatMode => {
    const paramMode = params?.[queryKeys.mode] as ChatMode | undefined;

    if (paramMode) {
      return paramMode;
    }

    if (chatMode !== 'research') {
      return chatMode;
    }

    return isBasicUser ? 'quickAnalysis' : 'research';
  };

  return {
    navigateToAlfred,
    mode: getMode(),
    previousMode,
    setMode,
    navigateToConversation,
    resetPreviousMode,
    alfredTrackingSource,
    setAlfredTrackingSource,
    clearAlfredTrackingSource,
  };
};
